import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/intl";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ClientPicker } from "@/components/resources/SendPickers";
import { cn } from "@/lib/utils";
import { errMsg } from "./kpiFormat";

const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-");
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
const usesText = (b) => plural(b.uses, "booking");
const visitsText = (b) => `${plural(b.visits, "visit")} with no pack or payment${b.last_visit ? ` · last ${fmtDate(b.last_visit)}` : ""}`;

const Picks = ({ title, rows, pick, onPick, detail, testId }) => !rows?.length ? null : (
  <div className="space-y-1.5" data-testid={testId}>
    <div className="text-2xs font-semibold uppercase tracking-[0.16em] text-zinc-500">{title}</div>
    <div className="flex flex-wrap gap-2">
      {rows.map((b) => (
        <button key={b.client_id} type="button" onClick={() => onPick({ email: b.client_id, name: b.name })}
          className={cn("rounded-full border px-3 py-1 text-left text-xs transition-colors hover:border-zinc-900",
            pick?.email === b.client_id && "border-zinc-900 bg-zinc-900 text-white")}
          data-testid={`${testId}-${slug(b.client_id)}`}>{b.name || b.client_id} · {detail(b)}</button>
      ))}
    </div>
  </div>
);

const Suggestions = ({ sug, pick, onPick }) => {
  if (!sug) return <div className="h-12 animate-pulse rounded bg-zinc-100" data-testid="kpi-owner-suggestions-loading" />;
  if (!sug.booked.length && !sug.attendees.length) {
    return <p className="text-xs text-zinc-500" data-testid="kpi-owner-no-suggestions">Nobody has booked with this code or attended without a pack since it started — search for the client below.</p>;
  }
  return (
    <div className="space-y-3">
      <Picks title="Booked with this code" rows={sug.booked} pick={pick} onPick={onPick} detail={usesText} testId="kpi-owner-booking-pick" />
      <Picks title={`Attended since ${fmtDate(sug.since)} without a pack`} rows={sug.attendees} pick={pick} onPick={onPick}
        detail={visitsText} testId="kpi-owner-attendee-pick" />
    </div>
  );
};

/** Pick who holds a pack: suggested people first (bookings with its code, then unpaid attendees), then any client. */
export const PackAssignDialog = ({ row, onClose, onSaved }) => {
  const [pick, setPick] = useState(null);
  const [note, setNote] = useState(row.note || "");
  const [busy, setBusy] = useState(false);
  const [sug, setSug] = useState(null);
  useEffect(() => {
    api.get(`/kpi/entitlements/${encodeURIComponent(row.id)}/suggestions`).then((r) => setSug(r.data))
      .catch(() => setSug({ booked: [], attendees: [] }));
  }, [row.id]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/kpi/entitlements/${encodeURIComponent(row.id)}/owner`, { client_email: pick.email, client_name: pick.name, note });
      toast.success(`${row.product} assigned to ${pick.name || pick.email}`);
      onSaved(pick);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="kpi-owner-dialog">
        <DialogHeader>
          <DialogTitle>Who holds this pack?</DialogTitle>
          <DialogDescription>{row.product}{row.code ? ` · code ${row.code}` : ""}. Your choice is kept after every sync, even if Acuity later names someone else.</DialogDescription>
        </DialogHeader>
        {pick ? (
          <div className="flex items-center justify-between rounded-md border border-zinc-200 px-3 py-2 text-sm" data-testid="kpi-owner-picked">
            <span><b>{pick.name || pick.email}</b> {pick.name && <span className="text-xs text-zinc-500">{pick.email}</span>}</span>
            <button type="button" className="text-xs text-zinc-500 hover:text-zinc-900" onClick={() => setPick(null)} data-testid="kpi-owner-change-pick">Change</button>
          </div>
        ) : (
          <>
            <Suggestions sug={sug} pick={pick} onPick={setPick} />
            <ClientPicker onPick={(c) => c.email ? setPick(c) : toast.error("That client has no email address")} />
          </>
        )}
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional), e.g. bought at reception"
          maxLength={300} data-testid="kpi-owner-note" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="kpi-owner-cancel">Cancel</Button>
          <Button onClick={save} disabled={!pick || busy} data-testid="kpi-owner-save">Assign customer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
